<template>
  <section>
    <SCompanyCard
      v-bind="omit(data.companyCard, ['description', 'slug', 'id'])"
      logo-emphasized
      reviews-url="#company-wall-details-reviews"
    />
    <div class="company-wall-details-content__description">
      <template v-if="useCollapseAbleDescription">
        <SCollapse :expanded="showMore" :text="data.descriptionLong!" />
        <SButton
          action-type="quaternary"
          class="company-wall-details-content__read-more"
          size="inline"
          @click="showMore = !showMore"
        >
          {{
            showMore
              ? tr("main_site.company_wall.read_less")
              : tr("main_site.company_wall.read_more")
          }}
        </SButton>
      </template>
      <p v-else v-html="data.descriptionLong ?? data.companyCard.description" />
    </div>

    <CompanyWallDetailsPortfolio
      v-if="showPortfolio"
      :portfolio="data.portfolio"
    />
  </section>

  <section v-if="data.geoProductCategories.length">
    <SHeading level="2" size="2">
      {{ tr("main_site.company_wall.products_title") }}
    </SHeading>
    <CompanyWallProducts
      :customer-id="data.companyCard.id"
      :geo-product-categories="data.geoProductCategories"
    />
  </section>

  <section>
    <SHeading level="2" size="2">
      {{ tr("main_site.company_wall.characteristics_title") }}
    </SHeading>
    <CompanyWallDetailsCharacteristics
      :characteristics="data.characteristics"
    />
  </section>

  <section v-if="data.faqs.length">
    <SHeading level="2" size="2">
      {{ tr("main_site.company_wall.faq_title") }}
    </SHeading>
    <CompanyWallQuestions :questions="data.faqs" />
  </section>

  <section v-if="addressInfoCards.length">
    <SHeading level="2" size="2">
      {{ tr("main_site.company_wall.locations_title") }}
    </SHeading>
    <IconInfoCards :info-cards="addressInfoCards" />
  </section>
</template>

<script setup lang="ts">
import type { ObjectToCamel } from "ts-case-convert";

import { omit } from "radash";
import { computed, ref } from "vue";

import { SButton, SCollapse } from "@solvari/common-fe";
import { SCompanyCard, SHeading } from "@solvari/components";
import { useI18nMainSiteCompanyWall } from "@solvari/translations";

import CompanyWallDetailsCharacteristics from "~/features/company-wall/components/company-wall-details/content/CompanyWallDetailsCharacteristics.vue";
import CompanyWallDetailsPortfolio from "~/features/company-wall/components/company-wall-details/content/CompanyWallDetailsPortfolio.vue";
import CompanyWallProducts from "~/features/company-wall/components/company-wall-details/content/CompanyWallProducts.vue";
import CompanyWallQuestions from "~/features/company-wall/components/company-wall-details/content/CompanyWallQuestions.vue";
import IconInfoCards from "~/features/company-wall/components/company-wall-details/content/IconInfoCards.vue";

const props = defineProps<{
  customerName: string;
  data: ObjectToCamel<App.Data.CompanyWallData>;
}>();

const { tr } = useI18nMainSiteCompanyWall();

const showMore = ref(false);

const showPortfolio = computed(() => {
  const portfolio = props.data.portfolio;
  return portfolio.projects.length || portfolio.video;
});

const useCollapseAbleDescription = computed(
  () => !!props.data.descriptionLong && props.data.descriptionLong.length > 200,
);

const addressInfoCards = computed(() => {
  if (!props.data.locations.length) {
    return [];
  }
  return props.data.locations.map((address, index, locations) => {
    const title = (() => {
      if (index >= 1) {
        return address.city;
      }
      if (locations.length > 1) {
        return `${props.customerName} (${tr("main_site.company_wall.locations_main_location")})`;
      }
      return props.customerName;
    })();
    const description = `${address.street} ${address.houseNo}, ${address.zipCode} ${address.city}`;

    return {
      title,
      description,
      icon: "fa-regular fa-location-dot",
      color: false,
    };
  });
});
</script>

<style lang="postcss">
.company-wall-details-content {
  &__description {
    @apply mt-1 whitespace-pre-line text-lg leading-7 text-gray-500;
  }

  &__read-more {
    @apply mb-0.5;
  }
}
</style>
